import { useDispatch, useSelector } from 'react-redux';
import fixWebmDuration from 'fix-webm-duration';

import { preventUserFromCloseWindow, uploadMediaBegins } from '../../../actions/publishActions';
import { FLOWS, UPLOAD_FAILED, messageTypes } from '../../../constants/mediaConstants';
import { showAlert, showMessage } from '../../../actions/globalActions';
import { generateId } from '../../../services/stringHelperService';
import { useTranslation } from 'react-i18next';
import { useEffect, useRef, useState } from 'react';
import { BOTH_MODE, CAMERA_MODE, SCREEN_MODE } from '../../../components/ScreenRecoder/ScreenRecorderConfigModal';

interface UseScreenRecorderProps {
	mode: string;
	isPreviewing: boolean;
	previousModeRef: any;
	onUserHitNativeBrowserStopSharingButton: () => void;
	handleModeChange: (_mode: string) => void;
}

const INACTIVE = 'INACTIVE';
const RECORDING = 'RECORDING';
const PAUSED = 'PAUSED';

const AUDIO = 'audioinput';
const VIDEO = 'videoinput';

// Upper bound, not a target - the frame is sized from the shared surface, see
// getRecordingFrameSize. Capped because encode cost grows with pixel count.
const MAX_FRAME_WIDTH = 2560;
const MAX_FRAME_HEIGHT = 1440;

// Used only when the surface refuses to report its size.
const FALLBACK_FRAME_WIDTH = 1920;
const FALLBACK_FRAME_HEIGHT = 1080;

// The overlay geometry was designed against a 1080-high frame; staying
// proportional to that keeps it the same relative size at any frame size.
const OVERLAY_REFERENCE_HEIGHT = 1080;

const TARGET_FPS = 30;

const floorToEven = (value: number) => Math.floor(value / 2) * 2;

// The frame the encoder sees. Sized from the shared surface rather than fixed,
// because any fixed size scales somebody's screen up or down for nothing, and
// scaling up cannot add detail. Picked off a 32x18 grid, the smallest step that
// is both exactly 16:9 and even on each axis.
const FRAME_GRID_WIDTH = 32;
const FRAME_GRID_HEIGHT = 18;
const MAX_FRAME_STEPS = Math.min(MAX_FRAME_WIDTH / FRAME_GRID_WIDTH, MAX_FRAME_HEIGHT / FRAME_GRID_HEIGHT);

const getRecordingFrameSize = (sourceWidth: number, sourceHeight: number) => {
	if (!(sourceWidth > 0) || !(sourceHeight > 0)) {
		return { width: FALLBACK_FRAME_WIDTH, height: FALLBACK_FRAME_HEIGHT };
	}

	const scale = Math.min(MAX_FRAME_WIDTH / sourceWidth, MAX_FRAME_HEIGHT / sourceHeight, 1);
	const contentWidth = Math.max(2, floorToEven(sourceWidth * scale));
	const contentHeight = Math.max(2, floorToEven(sourceHeight * scale));
	const steps = Math.min(
		MAX_FRAME_STEPS,
		Math.ceil(Math.max(contentWidth / FRAME_GRID_WIDTH, contentHeight / FRAME_GRID_HEIGHT))
	);

	return { width: steps * FRAME_GRID_WIDTH, height: steps * FRAME_GRID_HEIGHT };
};

// A quality ceiling, not a target, which is why it looks absurd. Screen content
// is cheap to encode, so headroom mostly lowers the quantiser instead of
// spending bits: files still average 3-6 Mbps and only reach for the ceiling
// when the screen is really moving. Measured flat past 32, and raising it costs
// no CPU - encode cost tracks resolution and frame rate, not this number.
const VIDEO_BITS_PER_SECOND = 32000000;
const AUDIO_BITS_PER_SECOND = 128000;

// How often the recorder hands us a chunk. 200ms meant five events, and five
// React updates, every second for the whole recording.
const RECORDER_TIMESLICE_MS = 1000;

// VP9 first: measured both sharper and about 3x cheaper than VP8, because
// Chrome's VP9 realtime path is better optimised. Not the usual
// quality-for-CPU trade, so do not reorder expecting VP8 to be the cheap one.
const VIDEO_MIME_TYPES = ['video/webm;codecs=vp9,opus', 'video/webm;codecs=vp8,opus', 'video/webm'];

const SAFARI_VIDEO_MIME_TYPES = ['video/mp4;codecs=avc1.640028,mp4a.40.2', 'video/mp4'];

// 'video/webm;codecs=vp9,opus' -> 'webm'
const getFileExtension = (mimeType: string) => {
	const container = (mimeType || '').split(';')[0].split('/')[1];

	return container === 'x-matroska' ? 'mkv' : container || 'webm';
};

// Chrome clamps main-thread timers to 1Hz in a hidden or occluded tab, which is
// the normal case while screen recording. Worker timers are not clamped, so the
// tick comes from a worker; the drawing stays here, where the canvas lives.
const FRAME_TICKER_SOURCE = `
let timer = null;
self.onmessage = function (event) {
	const data = event.data || {};
	if (timer) {
		clearInterval(timer);
		timer = null;
	}
	if (data.stop) {
		return;
	}
	timer = setInterval(function () {
		self.postMessage(1);
	}, data.interval);
};
`;

// Returns the function that stops the ticker again.
const createFrameTicker = (intervalMs: number, onTick: () => void) => {
	try {
		const blobUrl = URL.createObjectURL(new Blob([FRAME_TICKER_SOURCE], { type: 'application/javascript' }));
		const worker = new Worker(blobUrl);

		worker.onmessage = () => onTick();
		worker.postMessage({ interval: intervalMs });

		return () => {
			worker.postMessage({ stop: true });
			worker.terminate();
			URL.revokeObjectURL(blobUrl);
		};
	} catch (e) {
		// A blob worker can be refused by a strict CSP. A throttled recording is
		// still better than none, so fall back to the main-thread timer.
		console.error('screen recorder: frame ticker worker unavailable', e);

		let timer: any;
		const loop = () => {
			timer = window.setTimeout(() => {
				onTick();
				loop();
			}, intervalMs);
		};

		loop();
		return () => clearTimeout(timer);
	}
};

// Capture at device resolution, bounded by the same cap as the frame. Asking
// for less would make the browser discard HiDPI detail before we ever saw it.
const DISPLAY_VIDEO_CONSTRAINTS = {
	width: { max: MAX_FRAME_WIDTH },
	height: { max: MAX_FRAME_HEIGHT },
	frameRate: { ideal: TARGET_FPS, max: TARGET_FPS },
};

const getCameraVideoConstraints = (deviceId: string) => ({
	width: { ideal: MAX_FRAME_WIDTH, max: MAX_FRAME_WIDTH },
	height: { ideal: MAX_FRAME_HEIGHT, max: MAX_FRAME_HEIGHT },
	aspectRatio: { ideal: 16 / 9 },
	frameRate: { ideal: TARGET_FPS, max: TARGET_FPS },
	deviceId,
});

// In camera-only mode the webcam stream is recorded directly, so it is the one
// source that is not composited and needs to arrive at the target size itself.

export const USER_DEVICES_INPUT = {
	AUDIO,
	VIDEO,
};

export const RECORDING_STATUS = {
	INACTIVE,
	RECORDING,
	PAUSED,
};

const useScreenRecorder = ({
	mode,
	isPreviewing,
	previousModeRef,
	onUserHitNativeBrowserStopSharingButton,
	handleModeChange,
}: UseScreenRecorderProps) => {
	const dispatch = useDispatch<any>();
	const accountId = useSelector((state) => (state as any).session.defaultAccountId);
	const { t: translator } = useTranslation();

	const [previewStream, setPreviewStream] = useState<MediaStream | null>();
	const [mediaRecorder, setMediaRecorder] = useState<any>();
	const [screenShareStreams, setScreenShareStream] = useState<any>({});
	const [recordingStatus, setRecordingStatus] = useState<string>(INACTIVE);
	const [duration, setRecordingDuration] = useState<number>(0);
	const mediaChunksRef = useRef<any[]>([]);
	const [availableInputs, setAvailableInputs] = useState({ videoIds: {}, audioIds: {} });
	const [selectedInput, setSelectedInput] = useState({ videoId: '', audioId: '' });
	const [startUploadProcess, setStartUploadProcess] = useState<boolean>(false);
	const countDownIntervalRef = useRef<any>();
	const previousAudioIdRef = useRef<string>('');
	const previousWebcamIdRef = useRef<string>('');

	const afterJobRef = useRef<any>();
	const [canvasStream, setCanvasStream] = useState<MediaStream | null>();

	const canvasElementRef = useRef<any>(document.createElement('canvas'));
	// willReadFrequently forces a software-backed canvas, and nothing here ever
	// reads pixels back - it was keeping the compositor off the GPU for nothing.
	const canvasContextRef = useRef<any>(canvasElementRef.current.getContext('2d', { alpha: false }));
	const stopFrameTickerRef = useRef<any>();
	// Locked when compositing starts and held for the whole recording.
	const frameSizeRef = useRef({ width: FALLBACK_FRAME_WIDTH, height: FALLBACK_FRAME_HEIGHT });
	// The surface size the frame was locked from, so a resize can be noticed.
	const lockedSourceSizeRef = useRef({ width: 0, height: 0 });
	// drawFrame runs from a worker tick, so it needs the status as a ref rather
	// than the state value captured in its closure.
	const recordingStatusRef = useRef<string>(INACTIVE);
	// The compositor owns its video elements rather than borrowing the modal's:
	// Chrome pauses an element that stops being rendered, which froze recordings
	// when the modal collapsed to the floating widget.
	const sourceVideoRef = useRef<HTMLVideoElement>();
	const webcamSourceVideoRef = useRef<HTMLVideoElement>();

	const debounceTimer = useRef<any>();

	const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);

	const initCameraMode = async () => {
		try {
			const userStream = await navigator.mediaDevices.getUserMedia({
				audio: {
					// echoCancellation: false,
					noiseSuppression: true,
					sampleRate: 44100,
					deviceId: selectedInput.audioId,
				},
				video: getCameraVideoConstraints(selectedInput.videoId),
			});

			const mediaRecorder = getMediaRecorder(userStream);

			if (userStream) {
				setPreviewStream(userStream);
			}

			if (mediaRecorder) {
				setMediaRecorder(mediaRecorder);
			}
		} catch (e: any) {
			console.error(e);
			dispatch(showMessage(translator('MODAL_SCREEN_RECORDER_ACCESS_CAMERA_PERMISSION_ERROR')));

			resetRecoder();
			handleModeChange(CAMERA_MODE);
		}
	};

	const initScreenShareMode = async (isNeedToReplaceScreenStream?: boolean) => {
		try {
			const { screenStream: initStream } = screenShareStreams;
			let screenStream = initStream;

			if (!screenStream || isNeedToReplaceScreenStream) {
				screenStream = await navigator.mediaDevices.getDisplayMedia({
					video: DISPLAY_VIDEO_CONSTRAINTS,
					audio: true,
					// @ts-ignore: Unreachable code error
					// this bellow property should be available,. Somehow ts keep complain
					selfBrowserSurface: 'include',
				});
				hintTrackForDetail(screenStream);
			}
			// screen stream contain screen & audio output., audioStream contain microphone input
			// need to use audioContext to merge these two stream
			const audioContext = new AudioContext();

			const audioStream = await navigator.mediaDevices.getUserMedia({
				audio: {
					// echoCancellation: false,
					noiseSuppression: true,
					sampleRate: 44100,
					deviceId: selectedInput.audioId,
				},
			});

			const audioSource = audioContext.createMediaStreamSource(audioStream);
			let displaySource;
			try {
				// incase user not allow to capture sound. Then just ignore
				displaySource = audioContext.createMediaStreamSource(screenStream);
			} catch (e) {
				console.error(e);
			}

			const dest = audioContext.createMediaStreamDestination();
			audioSource.connect(dest);
			displaySource && displaySource.connect(dest);

			const mixedStream = new MediaStream();
			mixedStream.addTrack(screenStream.getVideoTracks()[0]);
			mixedStream.addTrack(dest.stream.getAudioTracks()[0]);

			// The preview shows the raw screen; the recording goes through the
			// canvas so the encoder always gets an even-sized 16:9 frame.
			await setSourceVideoStream(sourceVideoRef, screenStream);
			await setSourceVideoStream(webcamSourceVideoRef, null);

			const surface = getSharedSurfaceSize(screenStream);
			startCompositing(surface.width, surface.height);
			const recordingStream = getCanvasRecordingStream(dest.stream.getAudioTracks()[0]);
			const mediaRecorder = getMediaRecorder(recordingStream);

			setCanvasStream(recordingStream);
			setScreenShareStream({ screenStream, audioStream });

			screenStream.getTracks().forEach((track: any) =>
				track.addEventListener('ended', () => {
					audioStream.getTracks().forEach((track: any) => track.stop());
					stopCompositing();
					if (mediaRecorder) {
						onUserHitNativeBrowserStopSharingButton?.();
						mediaRecorder.stop();
						setStartUploadProcess(true);
					}
				})
			);

			if (mixedStream) {
				setPreviewStream(mixedStream);
			}

			if (mediaRecorder) {
				setMediaRecorder(mediaRecorder);
			}
		} catch (e: any) {
			if (e.message === 'Permission denied') {
				dispatch(
					showMessage(
						translator('MODAL_SCREEN_RECORDER_ACCESS_SCREEN_PERMISSION_ERROR'),
						messageTypes.warning
					)
				);
			}

			resetRecoder();
			handleModeChange(CAMERA_MODE);
		}
	};

	const initBothMode = async (isNeedToReplaceScreenStream?: boolean) => {
		try {
			const { screenStream: initStream } = screenShareStreams;
			let screenStream = initStream;

			if (!screenStream || isNeedToReplaceScreenStream) {
				screenStream = await navigator.mediaDevices.getDisplayMedia({
					video: DISPLAY_VIDEO_CONSTRAINTS,
					audio: true,
					// @ts-ignore: Unreachable code error
					// this bellow property should be available,. Somehow ts keep complain
					selfBrowserSurface: 'include',
				});
				hintTrackForDetail(screenStream);
			}

			const webcamStream = await navigator.mediaDevices.getUserMedia({
				audio: {
					// echoCancellation: false,
					noiseSuppression: true,
					sampleRate: 44100,
					deviceId: selectedInput.audioId,
				},
				video: getCameraVideoConstraints(selectedInput.videoId),
			});

			setScreenShareStream({ screenStream, webcamStream });

			screenStream.getTracks().forEach((track: any) =>
				track.addEventListener('ended', () => {
					webcamStream.getTracks().forEach((track: any) => track.stop());
					stopCompositing();
					if (mediaRecorder) {
						onUserHitNativeBrowserStopSharingButton?.();
						mediaRecorder.stop();
						setStartUploadProcess(true);
					}
				})
			);

			if (screenStream) {
				setPreviewStream(screenStream);
			}

			await setSourceVideoStream(sourceVideoRef, screenStream);
			await setSourceVideoStream(webcamSourceVideoRef, webcamStream);

			const surface = getSharedSurfaceSize(screenStream);
			startCompositing(surface.width, surface.height);

			const audioContext = new AudioContext();
			const audioSource = audioContext.createMediaStreamSource(webcamStream);
			let audioStreamSource;
			try {
				// incase user not allow to capture sound. Then just ignore
				audioStreamSource = audioContext.createMediaStreamSource(screenStream);
			} catch (e) {
				console.error(e);
			}

			const dest = audioContext.createMediaStreamDestination();
			audioSource.connect(dest);
			audioStreamSource && audioStreamSource.connect(dest);

			const recordingStream = getCanvasRecordingStream(dest.stream.getAudioTracks()[0]);
			const mediaRecorder = getMediaRecorder(recordingStream);

			setCanvasStream(recordingStream);

			if (mediaRecorder) {
				setMediaRecorder(mediaRecorder);
			}
		} catch (e: any) {
			if (e.message === 'Permission denied') {
				dispatch(
					showMessage(
						translator('MODAL_SCREEN_RECORDER_ACCESS_SCREEN_PERMISSION_ERROR'),
						messageTypes.warning
					)
				);
			}

			resetRecoder();
			handleModeChange(CAMERA_MODE);
		}
	};

	const setSourceVideoStream = async (videoRef: any, stream?: MediaStream | null) => {
		if (!videoRef.current) {
			const video = document.createElement('video');
			video.muted = true;
			video.playsInline = true;
			videoRef.current = video;
		}

		const video = videoRef.current;
		video.srcObject = stream ? new MediaStream(stream.getVideoTracks()) : null;

		if (!stream) {
			return;
		}

		try {
			// Detached elements do not autoplay, and drawImage on a paused video
			// just repeats whichever frame it stopped on.
			await video.play();

			// The frame is sized from the surface, so it has to have reported a
			// size before compositing starts or the first lock falls back.
			if (!video.videoWidth || !video.videoHeight) {
				await new Promise<void>((resolve) => {
					const done = () => {
						video.removeEventListener('loadedmetadata', done);
						video.removeEventListener('resize', done);
						resolve();
					};

					video.addEventListener('loadedmetadata', done);
					video.addEventListener('resize', done);
					// never block startup on a surface that refuses to report
					setTimeout(done, 1000);
				});
			}
		} catch (e) {
			console.error(e);
		}
	};

	const releaseSourceVideos = () => {
		[sourceVideoRef, webcamSourceVideoRef].forEach((videoRef) => {
			if (videoRef.current) {
				videoRef.current.pause();
				videoRef.current.srcObject = null;
			}
		});
	};

	const prepareCanvas = (sourceWidth: number, sourceHeight: number) => {
		const canvas = canvasElementRef.current;
		const frame = getRecordingFrameSize(sourceWidth, sourceHeight);

		frameSizeRef.current = frame;
		lockedSourceSizeRef.current = { width: sourceWidth, height: sourceHeight };

		// Set once per recording. Assigning width/height resets the canvas
		// bitmap, so doing it on every frame - as this used to - reallocated the
		// backing store continuously.
		if (canvas.width !== frame.width || canvas.height !== frame.height) {
			canvas.width = frame.width;
			canvas.height = frame.height;
		}

		canvasContextRef.current.imageSmoothingEnabled = true;
		canvasContextRef.current.imageSmoothingQuality = 'high';
	};

	// Fits the source into the frame, filling whichever axis binds first, so bars
	// land only on the axis the source's shape forces. Letterboxing rather than
	// cropping is deliberate - never silently drop part of what was shared.
	const getContainedRect = (sourceWidth: number, sourceHeight: number) => {
		const frame = frameSizeRef.current;
		const scale = Math.min(frame.width / sourceWidth, frame.height / sourceHeight, 1);

		// Even numbers only, size and offset. An odd edge puts the content/bar
		// boundary mid chroma sample, which is the reported green/purple line.
		const width = Math.max(2, floorToEven(sourceWidth * scale));
		const height = Math.max(2, floorToEven(sourceHeight * scale));

		return {
			width,
			height,
			x: floorToEven((frame.width - width) / 2),
			y: floorToEven((frame.height - height) / 2),
		};
	};

	const drawWebcamOverlay = (context: any, webcamVideo: any, contentRect: any) => {
		const padding = Math.round((contentRect.height * 80) / OVERLAY_REFERENCE_HEIGHT);
		const length = Math.round(contentRect.height / 3);
		const radius = Math.round((length * 70) / 360);
		const x = contentRect.x + padding;
		const y = contentRect.y + contentRect.height - padding - length;
		// Square crop from the centre of the webcam frame, whichever way round it is
		const sourceLength = Math.min(webcamVideo.videoWidth, webcamVideo.videoHeight);

		context.save();
		context.beginPath();
		context.moveTo(x + radius, y);
		context.lineTo(x + length - radius, y);
		context.quadraticCurveTo(x + length, y, x + length, y + radius);
		context.lineTo(x + length, y + length - radius);
		context.quadraticCurveTo(x + length, y + length, x + length - radius, y + length);
		context.lineTo(x + radius, y + length);
		context.quadraticCurveTo(x, y + length, x, y + length - radius);
		context.lineTo(x, y + radius);
		context.quadraticCurveTo(x, y, x + radius, y);
		context.closePath();
		context.clip();
		context.drawImage(
			webcamVideo,
			Math.round((webcamVideo.videoWidth - sourceLength) / 2),
			Math.round((webcamVideo.videoHeight - sourceLength) / 2),
			sourceLength,
			sourceLength,
			x,
			y,
			length,
			length
		);
		context.restore();
	};

	const drawFrame = () => {
		// The ticker keeps running regardless, so bailing out here while the
		// source video still has no dimensions simply skips a frame.
		const mainVideo = sourceVideoRef.current;
		if (!mainVideo || !mainVideo.videoWidth || !mainVideo.videoHeight) {
			return;
		}

		// Follow the surface until recording starts, then freeze: resizing the
		// canvas mid-stream would change the track's resolution partway through.
		const locked = lockedSourceSizeRef.current;
		const isRecording = recordingStatusRef.current === RECORDING || recordingStatusRef.current === PAUSED;

		if (!isRecording && (locked.width !== mainVideo.videoWidth || locked.height !== mainVideo.videoHeight)) {
			prepareCanvas(mainVideo.videoWidth, mainVideo.videoHeight);
		}

		const context = canvasContextRef.current;
		const contentRect = getContainedRect(mainVideo.videoWidth, mainVideo.videoHeight);

		context.fillStyle = '#000000';
		context.fillRect(0, 0, frameSizeRef.current.width, frameSizeRef.current.height);
		context.drawImage(mainVideo, contentRect.x, contentRect.y, contentRect.width, contentRect.height);

		const webcamVideo = webcamSourceVideoRef.current;
		if (mode === BOTH_MODE && webcamVideo && webcamVideo.videoWidth && webcamVideo.videoHeight) {
			drawWebcamOverlay(context, webcamVideo, contentRect);
		}
	};

	// The track reports the surface size as soon as getDisplayMedia resolves,
	// which is before the video element has metadata.
	const getSharedSurfaceSize = (stream: any) => {
		const settings = stream?.getVideoTracks?.()[0]?.getSettings?.() || {};
		const video = sourceVideoRef.current;

		return {
			width: settings.width || video?.videoWidth || FALLBACK_FRAME_WIDTH,
			height: settings.height || video?.videoHeight || FALLBACK_FRAME_HEIGHT,
		};
	};

	const startCompositing = (sourceWidth: number, sourceHeight: number) => {
		// Switching modes re-runs the init functions, so without this every switch
		// left another draw loop running against the same canvas.
		stopDrawLoop();
		prepareCanvas(sourceWidth, sourceHeight);
		drawFrame();
		stopFrameTickerRef.current = createFrameTicker(1000 / TARGET_FPS, drawFrame);
	};

	const stopDrawLoop = () => {
		stopFrameTickerRef.current?.();
		stopFrameTickerRef.current = undefined;
	};

	const stopCompositing = () => {
		stopDrawLoop();
		releaseSourceVideos();
	};

	const getCanvasRecordingStream = (audioTrack?: MediaStreamTrack) => {
		const recordingStream = new MediaStream();
		const [canvasVideoTrack] = canvasElementRef.current.captureStream(TARGET_FPS).getVideoTracks();

		if (canvasVideoTrack) {
			// Tells the encoder to protect detail rather than frame rate - a screen
			// recording is text first and motion second.
			canvasVideoTrack.contentHint = 'detail';
			recordingStream.addTrack(canvasVideoTrack);
		}

		if (audioTrack) {
			recordingStream.addTrack(audioTrack);
		}

		return recordingStream;
	};

	const hintTrackForDetail = (stream: any) => {
		stream.getVideoTracks().forEach((track: any) => {
			track.contentHint = 'detail';
		});
	};

	const getSupportedMimeType = () => {
		const candidates = isSafari ? SAFARI_VIDEO_MIME_TYPES : VIDEO_MIME_TYPES;
		// Pin the codec rather than letting the container pick one, so the encoder
		// is never handed something different after a browser update.
		return candidates.find((mimeType) => MediaRecorder.isTypeSupported(mimeType)) ?? '';
	};

	const getMediaRecorder = (inputStream: any) => {
		return new MediaRecorder(inputStream, {
			mimeType: getSupportedMimeType(),
			videoBitsPerSecond: VIDEO_BITS_PER_SECOND,
			audioBitsPerSecond: AUDIO_BITS_PER_SECOND,
		});
	};

	const addCollectedDataToMediaChunk = (e: any) => {
		// Held in a ref: this fires for the whole length of the recording and has
		// no business re-rendering the modal every time.
		if (e.data && e.data.size > 0) {
			mediaChunksRef.current.push(e.data);
		}
	};

	const processUploadingMedia = (blobChunks: any) => {
		return new Promise((resolve, reject) => {
			// only trigger when start event has been called

			if (!blobChunks[0]) {
				reject(null);
				setStartUploadProcess(false);
				return;
			}

			// prevent safari called event multiple time for unknown reason
			debouncedUploading(() => {
				const recordedType = blobChunks[0].type;
				let blob = new Blob(blobChunks, {
					type: recordedType,
				});

				fixWebmDuration(blob, duration * 1000).then((fixedBlob) => {
					const fileName = `Recording - ${new Date().toDateString()}`;
					// From what was actually recorded, not from the user agent -
					// guessing is how a Safari .mp4 got uploaded named .webm.
					const extension = getFileExtension(recordedType);
					const mediaId = generateId();
					const file = new File([fixedBlob], `${mediaId}.${extension}`, {
						type: recordedType,
					});

					(file as any).title = `${fileName}.${extension}`;
					(file as any).filename = `${fileName}.${extension}`;

					dispatch(showMessage(translator('COMMON_YOUR_MEDIA_BEING_UPLOADED'), messageTypes.info));
					resolve({ id: mediaId, duration, type: 'addNew' });
					dispatch(preventUserFromCloseWindow(true));
					dispatch(
						uploadMediaBegins(
							accountId,
							FLOWS.qbrickStandard,
							'',
							null,
							file,
							'',
							`${fileName}.${extension}`
						)
					).then((data: any) => {
						if (data && data !== UPLOAD_FAILED) {
							dispatch(showMessage(translator('COMMON_YOUR_MEDIA_BEING_ENCODED'), messageTypes.info));
						} else {
							reject();
							dispatch(showAlert(UPLOAD_FAILED, messageTypes.error));
						}
						dispatch(preventUserFromCloseWindow(false));
					});

					setStartUploadProcess(false);
					resetRecoder();
				});
			}, 500);
		});
	};

	const startDurationTimer = () => {
		countDownIntervalRef.current = setInterval(() => {
			setRecordingDuration((oldDuration) => oldDuration + 1);
		}, 1000);
	};

	const stopDurationTimer = () => {
		clearInterval(countDownIntervalRef.current);
	};

	const startRecording = () => {
		if (!previewStream) {
			return;
		}

		setRecordingStatus(RECORDING);
		mediaRecorder.start(RECORDER_TIMESLICE_MS);
		startDurationTimer();
	};

	const pauseRecording = () => {
		mediaRecorder.pause();
		setRecordingStatus(PAUSED);
		stopDurationTimer();
	};

	const resumeRecording = () => {
		mediaRecorder.resume();
		setRecordingStatus(RECORDING);
		startDurationTimer();
	};

	const stopRecording = ({
		isKeepScreenStream,
		initUploadProcess,
	}: {
		isKeepScreenStream?: boolean;
		initUploadProcess?: boolean;
	}) => {
		setRecordingStatus(INACTIVE);
		stopDurationTimer();
		stopCompositing();

		if (canvasStream) {
			canvasStream.getTracks().forEach((track) => track.stop());
		}

		previewStream &&
			previewStream.getTracks().forEach((track) => {
				if (!isKeepScreenStream || track.kind !== 'video') {
					track.stop();
				}
			});

		mediaRecorder && mediaRecorder.stop();

		const { screenStream, audioStream, webcamStream } = screenShareStreams;

		if (!isKeepScreenStream && screenStream) {
			screenStream.getTracks().forEach((track: any) => track.stop());
		}

		if (audioStream) {
			audioStream.getTracks().forEach((track: any) => track.stop());
		}

		if (webcamStream) {
			webcamStream.getTracks().forEach((track: any) => track.stop());
		}

		setScreenShareStream(isKeepScreenStream ? { screenStream } : {});

		if (initUploadProcess) {
			setStartUploadProcess(true);
		}
	};

	const switchScreen = () => {
		const { screenStream, audioStream, webcamStream } = screenShareStreams;

		stopCompositing();

		if (canvasStream) {
			canvasStream.getTracks().forEach((track) => track.stop());
		}

		previewStream &&
			previewStream.getTracks().forEach((track) => {
				track.stop();
			});

		if (screenStream) {
			screenStream.getTracks().forEach((track: any) => track.stop());
		}

		if (audioStream) {
			audioStream.getTracks().forEach((track: any) => track.stop());
		}

		if (webcamStream) {
			webcamStream.getTracks().forEach((track: any) => track.stop());
		}

		if (mode === SCREEN_MODE) {
			initScreenShareMode(true);
		} else if (mode === BOTH_MODE) {
			initBothMode(true);
		}
	};

	const restartRecording = () => {
		stopDurationTimer();
		mediaRecorder && mediaRecorder.stop();
		setStartUploadProcess(false);
		setRecordingDuration(0);
		mediaChunksRef.current = [];
		setRecordingStatus(RECORDING);
		startDurationTimer();
		// Screen and both mode are recorded off the canvas; only the camera is
		// recorded straight from its own stream.
		const newMediaRecoder = getMediaRecorder(mode === CAMERA_MODE ? previewStream : canvasStream);

		newMediaRecoder.start(RECORDER_TIMESLICE_MS);
		setMediaRecorder(newMediaRecoder);
	};

	const updateUserInput = (type: any, value: any) => {
		if (type === USER_DEVICES_INPUT.AUDIO) {
			previousAudioIdRef.current = selectedInput.audioId;
		}

		if (type === USER_DEVICES_INPUT.VIDEO) {
			previousWebcamIdRef.current = selectedInput.videoId;
		}

		const idType = type === USER_DEVICES_INPUT.AUDIO ? 'audioId' : 'videoId';
		setSelectedInput({ ...selectedInput, [idType]: value });
	};

	const resetRecoder = () => {
		// called after error, or finised record
		setPreviewStream(null);
		setMediaRecorder(null);
		setScreenShareStream({});
		setRecordingDuration(0);
		mediaChunksRef.current = [];
		setCanvasStream(null);
		stopCompositing();
	};

	const debouncedUploading = (func: any, timeout = 300) => {
		clearTimeout(debounceTimer.current);
		debounceTimer.current = setTimeout(func, timeout);
	};

	useEffect(() => {
		recordingStatusRef.current = recordingStatus;
	}, [recordingStatus]);

	useEffect(() => {
		if (!isPreviewing || recordingStatus === RECORDING || recordingStatus === PAUSED) {
			return;
		}

		const isAudioInputUpdated = previousAudioIdRef.current !== selectedInput.audioId;
		const isVideoInputUpdated = previousWebcamIdRef.current !== selectedInput.videoId;
		const isSwitchBetweenScreenAdnBothMode = !`${previousModeRef.current}${mode}`.includes(CAMERA_MODE);

		const isKeepScreenStream = isAudioInputUpdated || isVideoInputUpdated || isSwitchBetweenScreenAdnBothMode;

		stopRecording({
			isKeepScreenStream,
		});
		switch (mode) {
			case CAMERA_MODE:
				initCameraMode();
				break;
			case SCREEN_MODE:
				initScreenShareMode();
				break;
			case BOTH_MODE:
				initBothMode();
				break;
		}
	}, [mode, isPreviewing, recordingStatus, selectedInput]);

	useEffect(() => {
		if (Object.keys(availableInputs.audioIds).length !== 0 || Object.keys(availableInputs.videoIds).length !== 0) {
			return;
		}

		navigator.mediaDevices.enumerateDevices().then((devices: any) => {
			const availableDevices = devices.reduce(
				(acc: any, { kind, groupId, label, deviceId }: any) => {
					if (!groupId || deviceId === 'default') {
						return acc;
					}

					if (kind !== USER_DEVICES_INPUT.AUDIO && kind !== USER_DEVICES_INPUT.VIDEO) {
						return acc;
					}

					if (kind === USER_DEVICES_INPUT.AUDIO) {
						acc.audioIds = { ...acc.audioIds, [groupId]: { label, deviceId } };
						return acc;
					}

					if (kind === USER_DEVICES_INPUT.VIDEO) {
						acc.videoIds = { ...acc.videoIds, [groupId]: { label, deviceId } };
						return acc;
					}
				},
				{ videoIds: {}, audioIds: {} }
			);

			setAvailableInputs(availableDevices);
		});
	}, [previewStream]);

	useEffect(() => {
		if (startUploadProcess) {
			processUploadingMedia(mediaChunksRef.current).then((media) => {
				media && afterJobRef?.current?.(media);
			});
		}
	}, [startUploadProcess]);

	useEffect(() => {
		if (!mediaRecorder) {
			return;
		}

		mediaRecorder.addEventListener('dataavailable', addCollectedDataToMediaChunk);
		return () => {
			mediaRecorder.removeEventListener('dataavailable', addCollectedDataToMediaChunk);
		};
	}, [mediaRecorder]);

	return {
		recordingStatus,
		previewStream,
		duration,
		selectedInput,
		availableInputs,
		screenShareStreams,
		afterJobRef,
		switchScreen,
		startRecording,
		pauseRecording,
		resumeRecording,
		stopRecording,
		restartRecording,
		updateUserInput,
		setScreenShareStream,
		resetRecoder,
	};
};
export default useScreenRecorder;
